import React, { useState, useEffect } from "react";
import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
  Image,
} from "@react-pdf/renderer";
import Logo from "./thumbnail_Lighthouse-Electric-stacked-black.png"
import { companyName } from '../../../configs';

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    paddingTop: 40,
    paddingHorizontal: 40,
    paddingBottom: 140,
    backgroundColor: "#ffffff",
    color: "#333",
  },
  header: {
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flexDirection: "column",
    alignItems: "center",
  },
  logoImage: {
    height: 80,
    width: 160,
  },
  serviceDivisionText: {
    fontSize: 12,
    color: '#374151',
    marginTop: 4,
  },
  contactInfoText: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 2,
  },
  companyName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#111827",
  },
  headerRight: {
    textAlign: "right",
    alignItems: "center",
  },
  invoiceTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#d1d5db",
    textTransform: "uppercase",
  },
  invoiceNumber: {
    fontSize: 10,
    color: "#6b7280",
    marginTop: 2,
  },
  detailsSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
    marginBottom: 30,
  },
  detailsBox: {
    width: "48%",
    backgroundColor: "#f9fafb",
    padding: 12,
    borderRadius: 6,
  },
  detailsTitle: {
    fontSize: 9,
    fontWeight: "600",
    color: "#4b5563",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  detailsText: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.5,
  },
  detailsTextBold: {
    fontWeight: "bold",
    color: "#111827",
  },
  infoGrid: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  infoGridLabel: {
    color: "#4b5563",
  },
  infoGridValue: {
    fontWeight: "500",
    color: "#111827",
  },
  table: {
    width: "100%",
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  tableHeaderCell: {
    padding: 8,
    fontWeight: "600",
    color: "#374151",
    fontSize: 9,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  tableCell: {
    padding: 8,
  },
  colDescription: { width: "80%" },
  colQty: { width: "15%", textAlign: "center" },
  categoryRow: {
    backgroundColor: "#e5e7eb",
    fontWeight: "600",
    padding: 6,
    color: "#1f2937",
    fontSize: 9,
  },
  summarySection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  summaryBox: {
    width: "35%",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  summaryLabel: {
    color: "#4b5563",
  },
  summaryValue: {
    fontWeight: "500",
    color: "#111827",
  },
  summaryTotalRow: {
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    marginTop: 4,
    paddingTop: 8,
  },
  summaryTotalLabel: {
    fontWeight: "bold",
    fontSize: 12,
  },
  summaryTotalValue: {
    fontWeight: "bold",
    fontSize: 12,
  },
  footer: {
    position: "absolute",
    bottom: 40,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    paddingTop: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    fontSize: 9,
    color: "#6b7280",
  },
  footerNotes: {
    width: "50%",
  },
  footerSignature: {
    width: "40%",
    textAlign: "center",
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#374151",
    height: 40,
    marginBottom: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signatureImage: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  footerTitle: {
    fontWeight: "600",
    color: "#374151",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  pageNumber: {
    position: "absolute",
    fontSize: 9,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
});

const Ticket = ({ data = {} }) => {
  const [signatureUrl, setSignatureUrl] = useState('');

  useEffect(() => {
    const signature = data?.inspectorSignature;
    if (typeof signature === 'string') {
      setSignatureUrl(signature);
    } else if (signature?.data) {
      setSignatureUrl(`data:image/png;base64,${signature.data}`);
    }
  }, [data]);
  let date = new Date(data.date);
  date.setDate(date.getDate() + 1);

  return (
    <Document title={data.invoiceNumber} producer={"Safe.Right.Fast"} pageMode={"fullScreen"} application={"T&M Tickets"} >
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            <Image src={Logo} style={styles.logoImage} />
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.invoiceTitle}>Ticket # {data.ticketNumStamp} </Text>
            <Text style={styles.serviceDivisionText}>Service Division</Text>
            <Text style={styles.contactInfoText}>Phone: (724) 873-3500</Text>
            <Text style={styles.contactInfoText}>Fax: (724) 916-0098</Text>
          </View>
        </View>

        {/* Details */}
        <View style={styles.detailsSection}>
          <View style={styles.detailsBox}>
            <Text style={styles.detailsTitle}>Description of Work</Text>
            <Text style={styles.detailsText}>{data.notes}</Text>
          </View>
          <View style={styles.detailsBox}>
            <Text style={styles.detailsTitle}>Details: {date.toLocaleDateString()}</Text>
            <Text style={styles.detailsTitle}>{data.job.customerName ? `${data.job.customerName}` : null}</Text>
            <Text style={styles.detailsTitle}>{data.job.jobName || null}</Text>
            <Text style={styles.detailsTitle}>{data.job.jobNumber ? `Job #: ${data.job.jobNumber}` : null}</Text>
            <View style={styles.infoGrid}>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader} fixed>
            <Text style={[styles.tableHeaderCell, styles.colDescription]}>
              Description
            </Text>
            <Text style={[styles.tableHeaderCell, styles.colQty]}>Qty</Text>
          </View>
          {/* Table Body */}
          {data.items.map((item, index) =>
            item.type === "category"
              ? (
                <View key={`category-${item.description}-${index}`} style={styles.categoryRow} wrap={false}>
                  <Text>{item.description}</Text>
                </View>
              )
              : (
                <View key={item.recordId || item.tempId || `item-${index}`} style={styles.tableRow} wrap={false}>
                  <Text style={[styles.tableCell, styles.colDescription]}>
                    {item.name} {item.note}
                  </Text>
                  <Text style={[styles.tableCell, styles.colQty]}>
                    {`${item.qty ? item.qty : item.quantity} ${item.unit || ''}`.trim()}
                  </Text>
                </View>
              )
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <View style={styles.footerNotes}>
            <Text style={styles.footerTitle}>Notes</Text>
            <Text>
              {`Thank you for your business.\n${data.followUp ? data.followUp : "If you have any questions, please contact us."}`}
            </Text>
          </View>
          <View style={styles.footerSignature}>
            <Text style={styles.footerTitle}>{`Authorized By: ${data.inspectorName}`}</Text>
            <View style={styles.signatureLine}>
              {signatureUrl ? (
                <Image src={signatureUrl} style={styles.signatureImage} />
              ) : null}
            </View>
            <Text>Signature</Text>
          </View>
        </View>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => (
            `${pageNumber} / ${totalPages}`
          )}
          fixed
        />
      </Page>
    </Document>
  );
}

export default Ticket;
